import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppStore } from "@/store/useAppStore";
import { Modal } from "@/components/common";
import { platforms } from "@/constants/platforms";
import { createManualReferenceLink } from "@/utils/mockData";
import { api } from "@/services/api";
import { LibraryMapper } from "@/services/mappers/libraryMapper";
import type { GEOScoreData } from "@/services/api";
import type { PlatformId } from "@/types";
import { ContentStudioLibraryLoader } from "@/services/contentStudioLibraryLoader";
import { LexicalMarkdownEditor } from "@/components/editor/LexicalMarkdownEditor";
import { useCreditEstimate } from "@/hooks/useCreditEstimate";
import { CreditEstimateScenario } from "@/services/creditEstimateService";
import {
  validateAndNavigate,
  getCurrentBalance,
} from "@/utils/validateCreditBalance";
// Simple Markdown Parser
const parseMarkdown = (text: string): string => {
  let html = text;

  // Handle tables first
  const tableRegex = /(\|.+\|[\r\n]+)+/g;
  html = html.replace(tableRegex, (table) => {
    const rows = table
      .trim()
      .split("\n")
      .filter((r) => r.trim());
    if (rows.length < 2) return table;

    let tableHtml = "<table>";
    rows.forEach((row, i) => {
      if (row.includes("---")) return;
      const cells = row.split("|").filter((c) => c.trim());
      if (i === 0) {
        tableHtml += `<thead><tr>${cells.map((c) => `<th>${c.trim()}</th>`).join("")}</tr></thead><tbody>`;
      } else {
        tableHtml += `<tr>${cells.map((c) => `<td>${c.trim()}</td>`).join("")}</tr>`;
      }
    });
    tableHtml += "</tbody></table>";
    return tableHtml;
  });

  // Headers
  html = html.replace(/^### (.*$)/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gm, "<h1>$1</h1>");

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Lists
  html = html.replace(/^\- (.*$)/gm, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, "<ul>$&</ul>");

  // Paragraphs
  const parts = html.split(/\n\n+/);
  html = parts
    .map((part) => {
      part = part.trim();
      if (!part) return "";
      if (
        part.startsWith("<h") ||
        part.startsWith("<table") ||
        part.startsWith("<ul")
      )
        return part;
      return `<p>${part.replace(/\n/g, "<br>")}</p>`;
    })
    .join("\n");

  return html;
};

// Topic Suggestions Component with API integration
const TopicSuggestions: React.FC<{
  topic: string;
  platforms: string[];
  targetPrompts: string[];
  onSelect: (suggestion: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}> = ({ topic, platforms, targetPrompts, onSelect, onLoadingChange }) => {
  const [suggestions, setSuggestions] = useState<
    Array<{
      id: string;
      title: string;
      description?: string;
      relevance_score: number;
      suggested_keywords?: string[];
    }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchSuggestions = async () => {
      setLoading(true);
      setError(null);
      onLoadingChange?.(true);

      try {
        const response = await api.topics.suggest({
          topic,
          platforms,
          target_prompts: targetPrompts,
          count: 5,
        });

        if (!cancelled && response.success) {
          setSuggestions(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch topic suggestions:", err);
          setError("Failed to load suggestions");
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    fetchSuggestions();
    return () => {
      cancelled = true;
    };
  }, [topic, JSON.stringify(platforms), JSON.stringify(targetPrompts)]);

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div
          className="analysis-spinner"
          style={{ width: "24px", height: "24px", margin: "0 auto" }}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "12px", color: "var(--danger)" }}>{error}</div>
    );
  }

  return (
    <>
      {suggestions.map((suggestion) => (
        <div
          key={suggestion.id}
          className="topic-suggestion-item"
          onClick={() => onSelect(suggestion.title)}
        >
          <div className="topic-suggestion-title">{suggestion.title}</div>
          <div className="topic-suggestion-meta">
            <span className="topic-suggestion-relevance">
              {Math.round(suggestion.relevance_score * 100)}% match
            </span>
            {suggestion.suggested_keywords && (
              <span className="topic-suggestion-keywords">
                {suggestion.suggested_keywords.slice(0, 3).join(" · ")}
              </span>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export const ContentPage: React.FC<{ libraryItemId?: string | null }> = ({
  libraryItemId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [topic, setTopic] = useState("");
  const [currentPlatform, setCurrentPlatform] = useState<PlatformId>("blog");
  // When opening a library item (e.g. from Content Monitor), default to Score/Review mode.
  const [isInputMode, setIsInputMode] = useState(() => !libraryItemId);
  const [libraryStatus, setLibraryStatus] = useState<
    "draft" | "published" | "scheduled"
  >("draft");
  const [isEditing, setIsEditing] = useState(false);
  const [editingMarkdown, setEditingMarkdown] = useState<string>("");
  const [isRescoring, setIsRescoring] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showTopicSuggestions, setShowTopicSuggestions] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);

  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({
    prompts: false,
    reflinks: false,
    platforms: false,
    skills: true,
  });

  // Skills data fetched from system skills API
  const [availableSkills, setAvailableSkills] = useState<
    { id: string; name: string; description: string; platforms: string[] }[]
  >([]);

  useEffect(() => {
    const categoryToPlatforms: Record<string, string[]> = {
      Universal: [],
      Blog: ["blog"],
      "X (Twitter)": ["twitter"],
      LinkedIn: ["linkedin"],
      Reddit: ["reddit"],
    };
    const toSlug = (name: string) =>
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    api.skills
      .listSystemItems()
      .then((res) => {
        if (res?.success && res.data) {
          const mapped = res.data.map((item: any) => ({
            id: toSlug(item.name),
            name: item.name,
            description: item.summary || "",
            platforms: categoryToPlatforms[item.category_name] ?? [],
          }));
          setAvailableSkills(mapped);
        }
      })
      .catch(() => {
        /* silently fail, skills section will be empty */
      });
  }, []);

  // Get default selected skills for a platform: platform-specific + universal
  const getDefaultSkillsForPlatform = (platform: string) => {
    return availableSkills
      .filter((s) => s.platforms.includes(platform) || s.platforms.length === 0)
      .map((s) => s.id);
  };

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [showAllRefLinks, setShowAllRefLinks] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingUrls, setPublishingUrls] = useState<Record<string, string>>(
    {},
  );

  const selectedPromptIds = useAppStore((state) => state.selectedPromptIds);
  const prompts = useAppStore((state) => state.prompts);
  const fetchPrompts = useAppStore((state) => state.fetchPrompts);
  const selectedPlatforms = useAppStore((state) => state.selectedPlatforms);
  const togglePlatform = useAppStore((state) => state.togglePlatform);
  const setSelectedPlatforms = useAppStore(
    (state) => state.setSelectedPlatforms,
  );
  const isGenerating = useAppStore((state) => state.isGenerating);
  const setGenerating = useAppStore((state) => state.setGenerating);
  const generatedContents = useAppStore((state) => state.generatedContents);
  const setGeneratedContents = useAppStore(
    (state) => state.setGeneratedContents,
  );
  const resetStudio = useAppStore((state) => state.resetStudio);
  const referenceLinks = useAppStore((state) => state.referenceLinks);
  const addReferenceLink = useAppStore((state) => state.addReferenceLink);
  const removeReferenceLinkFromAPI = useAppStore(
    (state) => state.removeReferenceLinkFromAPI,
  );
  const loadCitationsFromPrompts = useAppStore(
    (state) => state.loadCitationsFromPrompts,
  );
  const togglePromptSelection = useAppStore(
    (state) => state.togglePromptSelection,
  );
  const clearPromptSelection = useAppStore(
    (state) => state.clearPromptSelection,
  );
  const upsertLibraryItem = useAppStore((state) => state.upsertLibraryItem);

  const selectedPrompts = prompts.filter((p) =>
    selectedPromptIds.includes(p.id),
  );

  // Credit estimates for Content Studio
  const {
    formattedEstimate: suggestEstimate,
    requiredMinimumBalance: suggestRequired,
  } = useCreditEstimate({
    enabled: true,
    scenario: CreditEstimateScenario.CONTENT_STUDIO_TOPIC_SUGGESTION,
    params: { count: 5 },
    debounceMs: 500,
  });

  const {
    formattedEstimate: generateEstimate,
    requiredMinimumBalance: generateRequired,
  } = useCreditEstimate({
    enabled: selectedPlatforms.length > 0,
    scenario: CreditEstimateScenario.CONTENT_STUDIO_GENERATE_CONTENT,
    params: { platforms: selectedPlatforms.length },
    debounceMs: 500,
  });

  // Track whether platform change is from library load (skip auto-select in that case)
  const skipSkillAutoSelect = useRef(false);

  // Auto-select skills when platform changes or skills load
  useEffect(() => {
    if (skipSkillAutoSelect.current) {
      skipSkillAutoSelect.current = false;
      return;
    }
    if (availableSkills.length > 0) {
      setSelectedSkills(getDefaultSkillsForPlatform(currentPlatform));
    }
  }, [currentPlatform, availableSkills]);
  const completedPrompts = prompts.filter((p) => !p.loading);

  useEffect(() => {
    // Ensure prompts are loaded for Target Prompts selector
    if (prompts.length === 0) {
      fetchPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (libraryItemId) {
      setIsInputMode(false);
    }
  }, [libraryItemId]);

  // Polling timer must be stopped when route changes (including /content -> /content/:id) or unmount.
  const pollIntervalRef = useRef<number | null>(null);
  // Track previous libraryItemId to detect navigation from detail to create view
  const prevLibraryItemIdRef = useRef<string | null | undefined>(undefined);
  const clearPolling = () => {
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    // Stop any ongoing polling when switching routes (e.g., navigating to detail)
    clearPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      clearPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset studio when navigating from detail view (/content/:id) to create view (/content)
  useEffect(() => {
    if (
      prevLibraryItemIdRef.current !== undefined && // Not first render
      prevLibraryItemIdRef.current !== null && // Was viewing a detail
      libraryItemId === null // Now on create page
    ) {
      resetStudio();
      setTopic("");
      setIsInputMode(true);
    }
    prevLibraryItemIdRef.current = libraryItemId;
  }, [libraryItemId, resetStudio]);

  // Ensure only blog platform is selected by default (restrict to blog only)
  useEffect(() => {
    if (!libraryItemId && selectedPlatforms.length === 0) {
      setSelectedPlatforms(["blog"]);
    } else if (
      !libraryItemId &&
      (!selectedPlatforms.includes("blog") || selectedPlatforms.length > 1)
    ) {
      // If non-blog platforms are selected, reset to only blog
      setSelectedPlatforms(["blog"]);
    }
  }, []);

  useEffect(() => {
    if (!libraryItemId) return;

    const load = async () => {
      try {
        const result =
          await ContentStudioLibraryLoader.loadAsGeneratedContents(
            libraryItemId,
          );
        setTopic(result.topic);
        setSelectedPlatforms(result.platforms);
        setGeneratedContents(result.generatedContents);
        setIsInputMode(false);
        setLibraryStatus(result.status);
        if (result.activeSkills && result.activeSkills.length > 0) {
          setSelectedSkills(result.activeSkills);
        }
        if (result.currentPlatform) {
          skipSkillAutoSelect.current = true;
          setCurrentPlatform(result.currentPlatform as PlatformId);
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Failed to load library item into studio:", error);
      }
    };

    load();
  }, [libraryItemId, setGeneratedContents, setSelectedPlatforms]);

  useEffect(() => {
    if (!libraryItemId) return;
    if (!isEditing) return;
    const current = generatedContents[currentPlatform];
    setEditingMarkdown(current?.markdownSource || current?.content || "");
  }, [libraryItemId, isEditing, currentPlatform, generatedContents]);

  const handleUpdateLibraryContent = async () => {
    if (!libraryItemId) return;

    try {
      const existingScore = generatedContents[currentPlatform]?.score ?? 0;
      const saveResp = await api.library.save({
        content_id: String(libraryItemId),
        topic,
        platform: currentPlatform,
        content: editingMarkdown,
        // Keep existing score; editing does not trigger re-scoring.
        geo_score: existingScore,
        status: libraryStatus,
      });

      if (saveResp.success) {
        upsertLibraryItem(LibraryMapper.toContentItem(saveResp.data));
        const breakdown = (saveResp.data.platforms as any)?.[currentPlatform]
          ?.geo_breakdown as GEOScoreData | undefined;
        const nextScore = (saveResp.data.platforms as any)?.[currentPlatform]
          ?.score as number | undefined;

        setGeneratedContents({
          ...generatedContents,
          [currentPlatform]: {
            ...generatedContents[currentPlatform],
            content: editingMarkdown,
            markdownSource: editingMarkdown,
            score: breakdown?.total_score ?? nextScore ?? 0,
            checkResults: breakdown
              ? convertGEOScoreToCheckResults(breakdown)
              : (generatedContents[currentPlatform]?.checkResults ?? []),
            detectedSkills:
              breakdown?.detected_skills ??
              generatedContents[currentPlatform]?.detectedSkills ??
              [],
          },
        });
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Failed to update content:", error);
      alert("Update failed, please try again");
    }
  };

  // Load citations from selected prompts via API
  useEffect(() => {
    loadCitationsFromPrompts(selectedPromptIds);
  }, [selectedPromptIds, prompts]);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    if (selectedPlatforms.length === 0) return;

    // Check credit balance before generation
    const currentBalance = getCurrentBalance();
    if (
      generateRequired &&
      !validateAndNavigate(generateRequired, currentBalance, navigate)
    ) {
      return; // Insufficient balance, modal shown
    }

    // Prevent multiple concurrent polling loops
    clearPolling();
    setGenerating(true);
    setGeneratedContents({}); // 清空已有内容，触发 loading 状态显示

    try {
      const targetPrompts = selectedPrompts.map((p) => p.text).filter(Boolean);

      // Prepare citation refs for API (include source to distinguish auto vs manual)
      const citationRefs = referenceLinks.map((link) => ({
        id: link.id,
        title: link.title,
        url: link.url,
        source: link.source as "auto" | "manual",
      }));

      // Call content generation API (async - returns immediately with generation_id)
      const response = await api.content.generate({
        topic,
        platforms: selectedPlatforms,
        citations: citationRefs,
        target_prompts: targetPrompts,
        active_skills: selectedSkills,
      });

      if (!response.success) {
        throw new Error("Failed to start content generation");
      }

      const genId = response.generation_id;
      const libraryItemIdFromStart = response.library_item_id ?? null;

      // Poll for status until completion or failure
      let pollCount = 0;
      const maxPolls = 300; // Maximum 5 minutes (1 second intervals)
      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const statusResponse = await api.content.getStatus(genId);

          if (!statusResponse.success) {
            clearInterval(pollInterval);
            setGenerating(false);
            alert("Failed to get generation status");
            return;
          }

          const { status, results, error_message } = statusResponse;
          const libraryItemIdFromStatus =
            statusResponse.library_item_id ?? null;

          if (status === "completed" && results) {
            // Clear timer BEFORE processing results (as requested)
            clearInterval(pollInterval);
            pollIntervalRef.current = null;

            // Generation is now directly linked to Content Library on the backend.
            // Navigate to the detail page (Content Studio review mode).
            const toId = libraryItemIdFromStatus ?? libraryItemIdFromStart;
            if (toId) {
              setGenerating(false);
              navigate(`/app/content/${toId}`);
              return;
            }

            // Fallback: if backend didn't return library id, keep old behavior (render in-place)
            const newGeneratedContents: Record<string, any> = {};
            for (const [platform, data] of Object.entries(results)) {
              const checkResults = convertGEOScoreToCheckResults(
                data.geo_score,
              );
              newGeneratedContents[platform] = {
                content: data.content,
                score: data.geo_score.total_score,
                checkResults,
                detectedSkills: data.detected_skills,
              };
            }
            setGeneratedContents(newGeneratedContents);
            setIsInputMode(false);
            setCurrentPlatform(selectedPlatforms[0] as PlatformId);
            setGenerating(false);
          } else if (status === "failed") {
            // Clear timer BEFORE showing error (as requested)
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setGenerating(false);
            const errorMsg = error_message || "Content generation failed";
            console.error("Content generation failed:", errorMsg);
            alert(`内容生成失败：${errorMsg}`);
          } else if (pollCount >= maxPolls) {
            // Timeout - clear timer before showing error
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setGenerating(false);
            alert("内容生成超时，请重试");
          }
        } catch (error) {
          console.error("Polling error:", error);
          // Continue polling on transient errors
        }
      }, 1000); // Poll every second
      pollIntervalRef.current = pollInterval as unknown as number;
    } catch (error) {
      console.error("Failed to start content generation:", error);
      alert("内容生成失败，请重试");
      setGenerating(false);
    }
  };

  /**
   * Convert GEOScoreData from API to checkResults format
   */
  const convertGEOScoreToCheckResults = (geoScore: GEOScoreData) => {
    const checkResults: any[] = [];

    for (const breakdown of geoScore.breakdown) {
      for (const check of breakdown.checks) {
        checkResults.push({
          id: `${breakdown.category}-${check.name}`
            .toLowerCase()
            .replace(/\s+/g, "-"),
          label: check.name,
          desc: check.description || "",
          weight: check.weight,
          passed: check.passed,
          score: check.score,
        });
      }
    }

    return checkResults;
  };

  const handleAddManualRefLink = async () => {
    const input = document.getElementById("ref-link-input") as HTMLInputElement;
    const raw = input?.value.trim();
    if (!raw) return;

    // Allow users to paste either a full URL or a bare domain/path.
    const url =
      raw.startsWith("http://") || raw.startsWith("https://")
        ? raw
        : `https://${raw}`;
    try {
      // Validate/normalize URL. (User input boundary)
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      return;
    }

    if (referenceLinks.some((l) => l.url === url)) {
      return;
    }

    const linkData = createManualReferenceLink(url);

    try {
      // Call API to create citation
      const response = await api.citations.create({
        url: linkData.url!,
        title: linkData.title,
        domain: linkData.domain,
      });

      if (response.success) {
        // Add the citation with the API-returned ID
        addReferenceLink({
          ...linkData,
          id: response.data.id,
        });
      }
    } catch (error) {
      console.error("Failed to create citation:", error);
      alert("添加引用链接失败，请重试");
    }

    if (input) input.value = "";
  };

  const openPublishModal = async () => {
    if (!libraryItemId) return;
    try {
      const resp = await api.library.getById(libraryItemId);
      if (!resp.success) return;
      const item = LibraryMapper.toContentItem(resp.data);
      const urls: Record<string, string> = {};
      for (const [platform, data] of Object.entries(item.platforms || {})) {
        const d: any = data;
        urls[platform] = (d.publishedUrl as string) || "";
      }
      setPublishingUrls(urls);
      setShowPublishModal(true);
    } catch (e) {
      console.error("Failed to open publish modal:", e);
    }
  };

  const handlePublishContent = async () => {
    if (!libraryItemId) return;
    if (isPublishing) return;

    // If publishing, update status then open modal to fill URLs
    if (libraryStatus !== "published") {
      try {
        setIsPublishing(true);
        const resp = await api.library.updateStatus(libraryItemId, "published");
        if (resp.success) {
          upsertLibraryItem(LibraryMapper.toContentItem(resp.data));
          setLibraryStatus("published");
          // Open modal to fill URLs after publishing
          await openPublishModal();
        }
      } catch (e) {
        console.error("Failed to publish:", e);
      } finally {
        setIsPublishing(false);
      }
    }
  };

  const handleSavePublishLinks = async () => {
    if (!libraryItemId) return;
    let last: any = null;
    try {
      for (const [platform, url] of Object.entries(publishingUrls)) {
        const res = await api.library.updatePlatformPublishedUrl(
          libraryItemId,
          platform,
          url.trim() ? url.trim() : null,
        );
        if (res?.success) last = res;
      }
      if (last?.success && last.data) {
        const mapped = LibraryMapper.toContentItem(last.data);
        upsertLibraryItem(mapped);
        // Update local state with published URLs
        const urls: Record<string, string> = {};
        for (const [platform, data] of Object.entries(mapped.platforms || {})) {
          const d: any = data;
          urls[platform] = (d.publishedUrl as string) || "";
        }
        setPublishingUrls(urls);
      }
      setShowPublishModal(false);
    } catch (error) {
      console.error("Failed to save publish links:", error);
    }
  };

  const handleUnpublish = async () => {
    if (!libraryItemId) return;
    if (isPublishing) return;
    try {
      setIsPublishing(true);
      const resp = await api.library.updateStatus(libraryItemId, "draft");
      if (resp.success) {
        upsertLibraryItem(LibraryMapper.toContentItem(resp.data));
        setLibraryStatus("draft");
      }
    } catch (e) {
      console.error("Failed to unpublish:", e);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleRescore = async () => {
    if (!libraryItemId) return;
    if (isEditing) return;
    if (isRescoring) return;
    const current = generatedContents[currentPlatform];
    const contentToScore = current?.markdownSource || current?.content || "";
    if (!contentToScore.trim()) return;

    try {
      setIsRescoring(true);
      const saveResp = await api.library.save({
        content_id: String(libraryItemId),
        topic,
        platform: currentPlatform,
        content: contentToScore,
        geo_score: current?.score ?? 0,
        rescore: true,
        active_skills: selectedSkills,
        status: libraryStatus,
      });

      if (saveResp.success) {
        upsertLibraryItem(LibraryMapper.toContentItem(saveResp.data));
        const breakdown = (saveResp.data.platforms as any)?.[currentPlatform]
          ?.geo_breakdown as GEOScoreData | undefined;
        const nextScore = (saveResp.data.platforms as any)?.[currentPlatform]
          ?.score as number | undefined;

        setGeneratedContents({
          ...generatedContents,
          [currentPlatform]: {
            ...generatedContents[currentPlatform],
            score:
              breakdown?.total_score ??
              nextScore ??
              generatedContents[currentPlatform]?.score ??
              0,
            checkResults: breakdown
              ? convertGEOScoreToCheckResults(breakdown)
              : (generatedContents[currentPlatform]?.checkResults ?? []),
            detectedSkills:
              breakdown?.detected_skills ??
              generatedContents[currentPlatform]?.detectedSkills ??
              [],
          },
        });
      }
    } catch (error) {
      console.error("Re-score failed:", error);
      alert("Failed to re-score, please try again.");
    } finally {
      setIsRescoring(false);
    }
  };

  const handleOptimize = async () => {
    const current = generatedContents[currentPlatform];
    if (!current) return;
    if (isOptimizing || isGenerating) return;

    const failedChecks = (current.checkResults || [])
      .filter((item: any) => !item.passed)
      .map((item: any) => `- ${item.label}: ${item.desc}`);

    if (failedChecks.length === 0) return;

    const originalContent = current.content || "";
    const optimizePrompt = [
      "Below is the original article that needs optimization:",
      "",
      "<original_content>",
      originalContent,
      "</original_content>",
      "",
      "The following checklist items did NOT pass:",
      ...failedChecks,
      "",
      "Please revise the original article to fix the failed checks above.",
      "Keep the same topic, core structure, and existing strengths. Only improve the weak areas.",
      "Output the full revised article in Markdown.",
    ].join("\n");

    try {
      setIsOptimizing(true);

      const targetPrompts = selectedPrompts.map((p) => p.text).filter(Boolean);
      const citationRefs = referenceLinks.map((link) => ({
        id: link.id,
        title: link.title,
        url: link.url,
        source: link.source as "auto" | "manual",
      }));

      const response = await api.content.generate({
        topic,
        platforms: [currentPlatform],
        citations: citationRefs,
        target_prompts: targetPrompts,
        active_skills: selectedSkills,
        custom_prompt: optimizePrompt,
      });

      if (!response.success) {
        throw new Error("Failed to start optimization");
      }

      const genId = response.generation_id;

      // Poll for completion
      let pollCount = 0;
      const maxPolls = 300; // Maximum 5 minutes (1 second intervals)
      const pollInterval = setInterval(async () => {
        pollCount++;
        try {
          const statusResponse = await api.content.getStatus(genId);
          if (!statusResponse.success) {
            clearInterval(pollInterval);
            setIsOptimizing(false);
            return;
          }

          const { status, results } = statusResponse;
          const libraryId = statusResponse.library_item_id;

          if (status === "completed" && results) {
            clearInterval(pollInterval);
            setIsOptimizing(false);

            if (libraryId) {
              navigate(`/app/content/${libraryId}`);
              return;
            }

            // Fallback: render in-place
            const platformData = results[currentPlatform];
            if (platformData) {
              const checkResults = convertGEOScoreToCheckResults(
                platformData.geo_score as GEOScoreData,
              );
              setGeneratedContents({
                ...generatedContents,
                [currentPlatform]: {
                  content: platformData.content,
                  score: (platformData.geo_score as GEOScoreData).total_score,
                  checkResults,
                  detectedSkills: platformData.detected_skills,
                },
              });
            }
          } else if (status === "failed") {
            clearInterval(pollInterval);
            setIsOptimizing(false);
            alert("Optimization failed, please try again.");
          } else if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setIsOptimizing(false);
            alert("Optimization timed out, please try again.");
          }
        } catch {
          clearInterval(pollInterval);
          setIsOptimizing(false);
        }
      }, 1000);
    } catch (error) {
      console.error("Optimize failed:", error);
      setIsOptimizing(false);
      alert("Optimization failed, please try again.");
    }
  };

  const renderPromptSelectorTrigger = () => {
    if (completedPrompts.length === 0) {
      return (
        <div className="prompt-selector-trigger">
          <span className="prompt-selector-placeholder">
            No prompts available
          </span>
          <svg
            className="icon"
            style={{
              width: "16px",
              height: "16px",
              stroke: "var(--text-muted)",
            }}
          >
            <use href="#icon-plus"></use>
          </svg>
        </div>
      );
    }

    if (selectedPromptIds.length === 0) {
      return (
        <div
          className="prompt-selector-trigger"
          onClick={() => setShowPromptModal(true)}
        >
          <span className="prompt-selector-placeholder">
            Click to select prompts...
          </span>
          <svg
            className="icon"
            style={{
              width: "16px",
              height: "16px",
              stroke: "var(--text-muted)",
            }}
          >
            <use href="#icon-plus"></use>
          </svg>
        </div>
      );
    }

    return (
      <>
        <div className="prompt-selected-list">
          {selectedPrompts.map((p) => {
            const shortText =
              p.text.length > 40 ? p.text.substring(0, 40) + "..." : p.text;
            return (
              <div key={p.id} className="prompt-selected-item">
                <div className="prompt-selected-header">
                  <span className="prompt-selected-text">{shortText}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="prompt-selector-add"
          onClick={() => setShowPromptModal(true)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add more
        </div>
      </>
    );
  };

  const renderRefLinks = () => {
    // Display all links: both manually added and auto-imported from prompts (AI citations)
    const allLinks = referenceLinks;
    const MAX_VISIBLE_LINKS = 5;
    const visibleLinks = showAllRefLinks
      ? allLinks
      : allLinks.slice(0, MAX_VISIBLE_LINKS);
    const hiddenCount = allLinks.length - MAX_VISIBLE_LINKS;

    if (allLinks.length === 0) {
      return (
        <div className="ref-links-empty">
          No reference links yet. Add URLs or select prompts to auto-import AI
          citations.
        </div>
      );
    }

    const linkSvg = (
      <svg
        className="ref-link-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    );

    const deleteSvg = (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );

    const getSourceBadgeColor = (
      source?: "blog" | "reddit" | "twitter" | "linkedin" | "other",
    ) => {
      switch (source) {
        case "blog":
          return "blog";
        case "reddit":
          return "reddit";
        case "twitter":
          return "twitter";
        case "linkedin":
          return "linkedin";
        default:
          return "other";
      }
    };

    return (
      <>
        {visibleLinks.map((link) => (
          <div
            key={link.id}
            className={`ref-link-item ${link.autoImported ? "ref-link-auto" : ""}`}
          >
            {linkSvg}
            <div className="ref-link-content">
              <a
                className="ref-link-title"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                title={link.url}
                onClick={(e) => e.stopPropagation()}
              >
                {link.title || link.domain || link.url}
              </a>
              {link.domain && link.domain !== link.title && (
                <span className="ref-link-domain" title={link.url}>
                  {link.domain}
                </span>
              )}
            </div>
            <div className="ref-link-badges">
              {link.autoImported ? (
                <span className="ref-link-source-badge ai-citation">
                  AI Citation
                </span>
              ) : (
                <span className="ref-link-source-badge manual">Manual</span>
              )}
              {link.citationSource && link.citationSource !== "other" && (
                <span
                  className={`ref-link-source-badge ${getSourceBadgeColor(link.citationSource)}`}
                >
                  {link.citationSource === "blog"
                    ? "Blog"
                    : link.citationSource === "reddit"
                      ? "Reddit"
                      : link.citationSource === "twitter"
                        ? "Twitter"
                        : link.citationSource === "linkedin"
                          ? "LinkedIn"
                          : link.citationSource}
                </span>
              )}
            </div>
            <button
              className="ref-link-delete"
              onClick={() => removeReferenceLinkFromAPI(link.id)}
              title="Remove"
            >
              {deleteSvg}
            </button>
          </div>
        ))}
        {hiddenCount > 0 && (
          <div
            className="ref-links-toggle"
            onClick={() => setShowAllRefLinks(!showAllRefLinks)}
          >
            {showAllRefLinks ? (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 15l-6-6-6 6" />
                </svg>
                Show less
              </>
            ) : (
              <>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
                Show all {allLinks.length} links
              </>
            )}
          </div>
        )}
      </>
    );
  };

  const renderActiveSkills = () => {
    if (selectedSkills.length === 0) {
      return <div className="geo-skills-empty">No skills selected</div>;
    }

    return selectedSkills.map((skillId) => {
      const skill = availableSkills.find((s) => s.id === skillId);
      if (!skill) return null;

      return (
        <div
          key={skillId}
          className="geo-skill-tag active"
          title={skill.description}
        >
          <span className="skill-dot"></span>
          {skill.name}
        </div>
      );
    });
  };

  const renderChecklist = (_platform: string, results: any[]) => {
    const checkIcon = (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
    const xIcon = (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );

    return (
      <>
        {results.map((item) => {
          const status = item.passed ? "pass" : "fail";
          const icon = status === "pass" ? checkIcon : xIcon;
          return (
            <div key={item.id} className="geo-checklist-item">
              <div className={`geo-check-icon ${status}`}>{icon}</div>
              <div className="geo-check-content">
                <div className="geo-check-label">{item.label}</div>
                <div className="geo-check-desc">{item.desc}</div>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const currentContent = generatedContents[currentPlatform];
  const hasContent = Object.keys(generatedContents).length > 0;

  return (
    <>
      <div className="main-header">
        <div className="main-header-left">
          <h1 className="main-title" id="studio-title">
            Content Studio
          </h1>
          <span
            className="studio-subtitle"
            id="studio-subtitle"
            style={{ display: "none" }}
          ></span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {libraryItemId ? (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate("/app/library")}
            >
              Back to Monitor
            </button>
          ) : null}
          <button
            className="btn btn-secondary btn-sm"
            id="studio-new-btn"
            style={{ display: hasContent ? "flex" : "none" }}
            onClick={() => {
              resetStudio();
              setIsInputMode(true);
              setGeneratedContents({});
              setTopic("");
              navigate("/app/content");
            }}
          >
            <svg
              className="icon"
              style={{ width: "14px", height: "14px", marginRight: "6px" }}
            >
              <use href="#icon-plus"></use>
            </svg>
            Create
          </button>
        </div>
      </div>
      <div className="main-body main-body--content">
        <div
          className={`studio-layout ${isInputMode ? "input-mode" : "review-mode"}`}
        >
          {/* Expand Settings Button - visible in review mode */}
          {!isInputMode && (
            <button
              className="expand-settings-btn"
              onClick={() => setIsInputMode(true)}
            >
              Settings
            </button>
          )}
          {/* View Score Button - visible in input mode */}
          {isInputMode && hasContent && (
            <button
              className="expand-score-btn"
              onClick={() => setIsInputMode(false)}
            >
              Score
            </button>
          )}
          {/* Left: Configuration Panel */}
          <div className="studio-config">
            {/* Section 1: Target Prompts */}
            <div
              className={`config-section ${collapsedSections.prompts ? "collapsed" : ""}`}
            >
              <div
                className="config-section-header"
                onClick={() =>
                  setCollapsedSections({
                    ...collapsedSections,
                    prompts: !collapsedSections.prompts,
                  })
                }
              >
                <div className="config-section-title">
                  <span className="config-section-num">1</span>
                  Target Prompts
                </div>
                <span
                  className="config-optional"
                  id="prompts-optional"
                  style={{ display: hasContent ? "none" : "inline" }}
                >
                  Optional
                </span>
                <svg className="config-chevron" id="prompts-chevron">
                  <use href="#icon-chevron"></use>
                </svg>
              </div>
              <div className="config-section-body">
                <div
                  id="prompt-selector-content"
                  onClick={() => !isGenerating && setShowPromptModal(true)}
                  style={
                    isGenerating
                      ? { pointerEvents: "none", opacity: 0.5 }
                      : undefined
                  }
                >
                  {renderPromptSelectorTrigger()}
                </div>
              </div>
            </div>

            {/* Section 2: Topic */}
            <div className="config-section config-section-main">
              <div className="config-section-header no-collapse">
                <div className="config-section-title">
                  <span className="config-section-num">2</span>
                  Topic
                </div>
              </div>
              <div className="config-section-body">
                <div className="topic-input-wrap">
                  <input
                    type="text"
                    className="topic-input"
                    id="content-topic"
                    placeholder="What should we write about?"
                    value={topic}
                    disabled={isGenerating}
                    onChange={(e) => setTopic(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !isGenerating) {
                        handleGenerate();
                      }
                    }}
                  />
                  {!isSuggesting && (
                    <button
                      className="suggest-btn"
                      onClick={() => {
                        if (!showTopicSuggestions) {
                          const currentBalance = getCurrentBalance();
                          if (
                            suggestRequired &&
                            !validateAndNavigate(
                              suggestRequired,
                              currentBalance,
                              navigate,
                            )
                          ) {
                            return;
                          }
                        }
                        setShowTopicSuggestions(!showTopicSuggestions);
                      }}
                      disabled={isGenerating}
                    >
                      <svg
                        className="icon"
                        style={{ width: "14px", height: "14px" }}
                      >
                        <use href="#icon-sparkles"></use>
                      </svg>
                      Suggest
                      {suggestEstimate && (
                        <span
                          className="credit-estimate-hint"
                          style={{
                            marginLeft: "6px",
                            fontSize: "11px",
                            opacity: 0.7,
                          }}
                        >
                          {suggestEstimate}
                        </span>
                      )}
                    </button>
                  )}
                </div>
                {showTopicSuggestions && (
                  <div className="topic-suggestions" id="topic-suggestions">
                    <div className="topic-suggestions-header">
                      <svg
                        className="icon"
                        style={{
                          width: "14px",
                          height: "14px",
                          marginRight: "6px",
                        }}
                      >
                        <use href="#icon-sparkles"></use>
                      </svg>
                      AI-Generated Suggestions
                    </div>
                    <div className="topic-suggestions-list">
                      <TopicSuggestions
                        topic={topic}
                        platforms={selectedPlatforms}
                        onSelect={(suggestion) => {
                          setTopic(suggestion);
                          setShowTopicSuggestions(false);
                        }}
                        targetPrompts={selectedPrompts
                          .map((p) => p.text)
                          .filter(Boolean)}
                        onLoadingChange={setIsSuggesting}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Reference Links */}
            <div
              className={`config-section ${collapsedSections.reflinks ? "collapsed" : ""}`}
            >
              <div
                className="config-section-header"
                onClick={() =>
                  setCollapsedSections({
                    ...collapsedSections,
                    reflinks: !collapsedSections.reflinks,
                  })
                }
              >
                <div className="config-section-title">
                  <span className="config-section-num">3</span>
                  Reference Links
                </div>
                <div className="config-section-summary" id="reflinks-summary">
                  {referenceLinks.length} links
                </div>
                <svg
                  className={`config-chevron ${collapsedSections.reflinks ? "" : ""}`}
                  id="reflinks-chevron"
                >
                  <use href="#icon-chevron"></use>
                </svg>
              </div>
              <div className="config-section-body" id="reflinks-body">
                <div className="ref-links-list" id="ref-links-list">
                  {renderRefLinks()}
                </div>
                <div className="ref-links-add">
                  <input
                    type="text"
                    id="ref-link-input"
                    placeholder="Paste your preferred article URL..."
                    disabled={isGenerating}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !isGenerating) {
                        handleAddManualRefLink();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddManualRefLink}
                    disabled={isGenerating}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Section 4: Platforms */}
            <div
              className={`config-section ${collapsedSections.platforms ? "collapsed" : ""}`}
            >
              <div
                className="config-section-header"
                onClick={() =>
                  setCollapsedSections({
                    ...collapsedSections,
                    platforms: !collapsedSections.platforms,
                  })
                }
              >
                <div className="config-section-title">
                  <span className="config-section-num">4</span>
                  Platforms
                </div>
                <div className="config-section-summary" id="platforms-summary">
                  {selectedPlatforms
                    .map((p) => platforms[p]?.name)
                    .filter(Boolean)
                    .join(", ") || "None"}
                </div>
                <svg className="config-chevron" id="platforms-chevron">
                  <use href="#icon-chevron"></use>
                </svg>
              </div>
              <div className="config-section-body" id="platforms-body">
                <div className="platform-pills">
                  {Object.values(platforms).map((platform) => {
                    const isBlog = platform.id === "blog";
                    const isDisabled = !isBlog;
                    return (
                      <label
                        key={platform.id}
                        className={`platform-pill ${selectedPlatforms.includes(platform.id) ? "selected" : ""} ${isDisabled || isGenerating ? "disabled" : ""}`}
                        data-platform={platform.id}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isDisabled && !isGenerating) {
                            togglePlatform(platform.id);
                          }
                        }}
                        title={
                          isDisabled ? "Only blog is available for now" : ""
                        }
                      >
                        <svg
                          className="icon"
                          style={{ width: "16px", height: "16px" }}
                        >
                          <use
                            href={`#icon-${platform.id === "twitter" ? "twitter" : platform.id === "blog" ? "blog" : platform.id === "reddit" ? "reddit" : "linkedin"}`}
                          ></use>
                        </svg>
                        {platform.name}
                        {isDisabled && (
                          <svg
                            className="platform-lock-icon"
                            style={{
                              width: "10px",
                              height: "10px",
                              marginLeft: "4px",
                            }}
                          >
                            <use href="#icon-lock"></use>
                          </svg>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Section 5: Skills */}
            <div
              className={`config-section ${collapsedSections.skills ? "collapsed" : ""}`}
            >
              <div
                className="config-section-header"
                onClick={() =>
                  setCollapsedSections({
                    ...collapsedSections,
                    skills: !collapsedSections.skills,
                  })
                }
              >
                <div className="config-section-title">
                  <span className="config-section-num">5</span>
                  Skills
                </div>
                <div className="config-section-summary">
                  {selectedSkills.length} selected
                </div>
                <svg className="config-chevron">
                  <use href="#icon-chevron"></use>
                </svg>
              </div>
              <div className="config-section-body">
                <div className="skills-grid">
                  {availableSkills
                    .filter(
                      (s) =>
                        s.platforms.includes(currentPlatform) ||
                        s.platforms.length === 0,
                    )
                    .map((skill) => (
                      <label key={skill.id} className="skill-item">
                        <input
                          type="checkbox"
                          checked={selectedSkills.includes(skill.id)}
                          disabled={isGenerating}
                          onChange={() => {
                            setSelectedSkills((prev) =>
                              prev.includes(skill.id)
                                ? prev.filter((id) => id !== skill.id)
                                : [...prev, skill.id],
                            );
                          }}
                        />
                        <span className="skill-item-label">{skill.name}</span>
                        <span className="skill-item-desc">
                          {skill.description}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </div>

            {/* Sticky Generate Button */}
            <div className="config-generate-footer">
              <button
                className="btn btn-primary btn-generate"
                id="generate-btn"
                onClick={handleGenerate}
                disabled={
                  !topic.trim() ||
                  isGenerating ||
                  selectedPlatforms.length === 0
                }
              >
                <svg
                  className="icon"
                  style={{ width: "18px", height: "18px", marginRight: "8px" }}
                >
                  <use href="#icon-sparkles"></use>
                </svg>
                Generate Content
                {generateEstimate && (
                  <span
                    className="credit-estimate-hint"
                    style={{
                      marginLeft: "6px",
                      fontSize: "11px",
                      opacity: 0.7,
                    }}
                  >
                    {generateEstimate}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Center: Preview / Output */}
          <div className="studio-preview">
            {!hasContent ? (
              <div className="preview-empty" id="preview-empty">
                {isGenerating ? (
                  <div className="generation-loading">
                    <div className="generation-loading-pulse"></div>
                    <div className="generation-loading-header">
                      <h3>Generating content...</h3>
                      <p>
                        Applying {selectedSkills.length} skills to create
                        GEO-optimized content
                      </p>
                    </div>
                    <div className="generation-loading-steps">
                      {[
                        { label: "Analyzing topic & prompts", delay: "0s" },
                        { label: "Applying skills & structure", delay: "2s" },
                        { label: "Integrating references", delay: "4s" },
                        { label: "Optimizing for AI citability", delay: "6s" },
                      ].map((step, i) => (
                        <div
                          key={i}
                          className="generation-step"
                          style={{ animationDelay: step.delay }}
                        >
                          <span className="generation-step-dot"></span>
                          {step.label}
                        </div>
                      ))}
                    </div>
                    {selectedSkills.length > 0 && (
                      <div className="generation-loading-skills">
                        {selectedSkills.slice(0, 5).map((skillId) => {
                          const skill = availableSkills.find(
                            (s) => s.id === skillId,
                          );
                          return skill ? (
                            <span
                              key={skillId}
                              className="generation-skill-chip"
                            >
                              {skill.name}
                            </span>
                          ) : null;
                        })}
                        {selectedSkills.length > 5 && (
                          <span className="generation-skill-chip generation-skill-more">
                            +{selectedSkills.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="preview-knowhow">
                    <div className="knowhow-header">
                      <svg
                        className="icon"
                        style={{ width: "32px", height: "32px" }}
                      >
                        <use href="#icon-sparkles"></use>
                      </svg>
                      <h3>Skill-Based Multi-Agent Content Creation</h3>
                      <p>
                        We analyze your inputs, brand identity, competitor
                        citations, leveraging skill-based agents with proven GEO
                        expertise to maximize your AI citation rates
                      </p>
                    </div>
                    <div className="knowhow-features">
                      <div className="knowhow-item">
                        <div className="knowhow-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <div className="knowhow-content">
                          <h4>Learn from Prompts</h4>
                          <p>
                            Analyze your tracked prompts to understand what AI
                            systems are looking for
                          </p>
                        </div>
                      </div>
                      <div className="knowhow-item">
                        <div className="knowhow-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        </div>
                        <div className="knowhow-content">
                          <h4>Reference Integration</h4>
                          <p>
                            Weave your citations naturally into content for
                            credibility and authority
                          </p>
                        </div>
                      </div>
                      <div className="knowhow-item">
                        <div className="knowhow-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                        <div className="knowhow-content">
                          <h4>Brand-Aware Writing</h4>
                          <p>
                            Content is tailored to your brand tone, product
                            context, and company identity for a consistent voice
                          </p>
                        </div>
                      </div>
                      <div className="knowhow-item">
                        <div className="knowhow-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                          </svg>
                        </div>
                        <div className="knowhow-content">
                          <h4>
                            Generative Engine Optimization (GEO) Skills
                            Auto-Applied
                          </h4>
                          <p>
                            Proven writing techniques that boost AI citation
                            rates:
                          </p>
                          <div className="knowhow-skills-grid">
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Inverted Pyramid
                            </span>
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Direct Answers
                            </span>
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Data Tables
                            </span>
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Authority Signals
                            </span>
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Contextual Priming
                            </span>
                            <span className="knowhow-skill-tag">
                              <span className="skill-dot"></span>
                              Semantic Richness
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="preview-content" id="preview-content">
                <div className="preview-header">
                  <div className="preview-tabs" id="preview-tabs">
                    {selectedPlatforms.map((p) => {
                      const publishedUrl = generatedContents[p]?.publishedUrl;
                      return (
                        <div key={p} className="preview-tab-wrapper">
                          <button
                            className={`preview-tab ${currentPlatform === p ? "active" : ""}`}
                            data-platform={p}
                            onClick={() => setCurrentPlatform(p as PlatformId)}
                          >
                            <svg
                              className="icon"
                              style={{
                                width: "14px",
                                height: "14px",
                                marginRight: "4px",
                              }}
                            >
                              <use href={`#icon-${p}`}></use>
                            </svg>
                            {platforms[p].name}
                          </button>
                          {publishedUrl && (
                            <a
                              href={publishedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="platform-published-url"
                              title={publishedUrl}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg
                                className="icon"
                                style={{ width: "12px", height: "12px" }}
                              >
                                <use href="#icon-link"></use>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="preview-actions-bar">
                    {libraryStatus === "published" ? (
                      <>
                        <button
                          className="action-btn"
                          onClick={openPublishModal}
                          title="Edit publish links"
                          disabled={!libraryItemId}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                              ry="2"
                            />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          <span>Links</span>
                        </button>
                        <button
                          className="action-btn"
                          onClick={handleUnpublish}
                          title="Unpublish"
                          disabled={!libraryItemId || isPublishing}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                          <span>
                            {isPublishing ? "Unpublishing..." : "Unpublish"}
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        className="action-btn"
                        onClick={handlePublishContent}
                        title="Publish"
                        disabled={!libraryItemId || isPublishing}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <span>
                          {isPublishing ? "Publishing..." : "Publish"}
                        </span>
                      </button>
                    )}
                    {libraryItemId ? (
                      <>
                        <div className="action-divider"></div>
                        <button
                          className="action-btn"
                          onClick={handleRescore}
                          title="Recalculate GEO score"
                          disabled={isRescoring || isEditing}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 19V5" />
                            <path d="M4 19H20" />
                            <path d="M7 15l3-3 3 2 4-6" />
                          </svg>
                          <span>{isRescoring ? "Scoring..." : "Re-score"}</span>
                        </button>
                      </>
                    ) : null}
                    <div className="action-divider"></div>
                    {libraryItemId ? (
                      isEditing ? (
                        <>
                          <button
                            className="action-btn action-btn-primary"
                            onClick={handleUpdateLibraryContent}
                            title="Update content"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                              <polyline points="17 21 17 13 7 13 7 21" />
                              <polyline points="7 3 7 8 15 8" />
                            </svg>
                            <span>Update</span>
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => setIsEditing(false)}
                            title="Cancel editing"
                          >
                            <span>Cancel</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="action-btn"
                            onClick={handleOptimize}
                            disabled={isOptimizing || isGenerating}
                            title="Regenerate content with current skills"
                          >
                            {isOptimizing ? (
                              <>
                                <svg
                                  className="spin"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                <span>Regenerating…</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="23 4 23 10 17 10" />
                                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                </svg>
                                <span>Regenerate</span>
                              </>
                            )}
                          </button>
                          <button
                            className="action-btn action-btn-primary"
                            onClick={() => setIsEditing(true)}
                            title="Edit content"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                        </>
                      )
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <div className="preview-body">
                  {isOptimizing && (
                    <div className="optimize-overlay">
                      <div className="generation-loading compact">
                        <div className="generation-loading-pulse"></div>
                        <div className="generation-loading-header">
                          <h3>Optimizing content...</h3>
                          <p>
                            Fixing{" "}
                            {
                              (currentContent?.checkResults || []).filter(
                                (c: any) => !c.passed,
                              ).length
                            }{" "}
                            checklist items
                          </p>
                        </div>
                        <div className="generation-loading-steps">
                          {[
                            { label: "Analyzing failed checks", delay: "0s" },
                            { label: "Revising weak sections", delay: "2s" },
                            { label: "Re-applying skills", delay: "4s" },
                          ].map((step, i) => (
                            <div
                              key={i}
                              className="generation-step"
                              style={{ animationDelay: step.delay }}
                            >
                              <span className="generation-step-dot"></span>
                              {step.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className="content-editor"
                    id="content-editor"
                    style={{
                      opacity: isOptimizing ? 0.15 : 1,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    {libraryItemId && isEditing ? (
                      <LexicalMarkdownEditor
                        initialMarkdown={editingMarkdown}
                        onMarkdownChange={setEditingMarkdown}
                        placeholder="Edit your content..."
                      />
                    ) : (
                      <div
                        className="content-output markdown-content"
                        id="editor-output"
                        dangerouslySetInnerHTML={{
                          __html: currentContent
                            ? parseMarkdown(currentContent.content)
                            : "",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Analysis Panel */}
          <div className="studio-analysis" id="studio-analysis">
            <div className="analysis-header">
              <h3 className="analysis-title">GEO Analysis</h3>
            </div>
            <div className="analysis-body">
              {currentContent && (
                <>
                  <div className="geo-checklist" id="geo-checklist">
                    <div className="geo-checklist-header">
                      <div className="geo-section-title">
                        Optimization Checklist
                      </div>
                    </div>
                    <div className="geo-checklist-grid" id="geo-checklist-grid">
                      {renderChecklist(
                        currentPlatform,
                        currentContent.checkResults || [],
                      )}
                    </div>
                  </div>
                  <div className="geo-skills-section" id="geo-skills-section">
                    <div className="geo-section-title">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                      </svg>
                      Active Skills
                    </div>
                    <div className="geo-skills-list" id="geo-skills-list">
                      {renderActiveSkills()}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Publish Links Modal */}
      <Modal
        show={showPublishModal}
        onClose={() => {
          setShowPublishModal(false);
          setPublishingUrls({});
        }}
        title="Publish Links"
        size="xl"
      >
        {!libraryItemId ? (
          <div style={{ padding: "12px" }}>Loading...</div>
        ) : (
          <>
            <p className="modal-desc">
              Copy the content, upload externally, then paste the published
              link(s) here.
            </p>
            <div className="publish-links-list">
              {Object.entries(generatedContents).map(
                ([platform, contentData]) => {
                  const content =
                    contentData?.markdownSource || contentData?.content || "";
                  return (
                    <div key={platform} className="publish-platform-card">
                      <div className="publish-platform-header">
                        <div className="publish-platform-title">{platform}</div>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => navigator.clipboard.writeText(content)}
                          disabled={!content}
                        >
                          Copy
                        </button>
                      </div>
                      <div className="publish-platform-body">
                        <LexicalMarkdownEditor
                          initialMarkdown={content}
                          onMarkdownChange={() => {}}
                          placeholder=""
                          readOnly
                          minHeight={180}
                          maxHeight={260}
                        />
                      </div>
                      <div className="publish-platform-footer">
                        <input
                          type="text"
                          className="publish-url-input"
                          placeholder="Paste published URL..."
                          value={publishingUrls[platform] ?? ""}
                          onChange={(e) =>
                            setPublishingUrls((prev) => ({
                              ...prev,
                              [platform]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  );
                },
              )}
            </div>
            <div className="modal-footer modal-footer--right">
              <div className="modal-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowPublishModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSavePublishLinks}
                >
                  Save
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Prompt Selection Modal */}
      <Modal
        show={showPromptModal}
        onClose={() => setShowPromptModal(false)}
        title="Select Target Prompts"
        size="lg"
      >
        <p className="modal-desc">
          Choose which AI queries you want your content to rank for
        </p>
        {completedPrompts.length === 0 ? (
          <div className="modal-prompt-empty">
            <p>No prompts tracked yet.</p>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setShowPromptModal(false);
                navigate("/app/prompts");
              }}
            >
              Go to Prompt Tracking
            </button>
          </div>
        ) : (
          <>
            <div className="modal-prompt-list">
              {completedPrompts.map((p) => {
                const mentioned =
                  p.results?.filter((r) => r.mentioned).length || 0;
                const hasNegative = p.results?.some(
                  (r) => r.mentioned && r.sentiment === "negative",
                );
                const statusColor =
                  mentioned === 0
                    ? "danger"
                    : hasNegative
                      ? "warning"
                      : mentioned < 3
                        ? "warning"
                        : "success";
                const statusText =
                  mentioned === 0
                    ? "Not cited"
                    : hasNegative
                      ? "Negative sentiment"
                      : `Cited in ${mentioned}/4`;
                const isSelected = selectedPromptIds.includes(p.id);

                return (
                  <div
                    key={p.id}
                    className={`modal-prompt-item ${isSelected ? "selected" : ""}`}
                    onClick={() => togglePromptSelection(p.id)}
                  >
                    <div className="modal-prompt-checkbox">
                      <svg className="icon">
                        <use href="#icon-check"></use>
                      </svg>
                    </div>
                    <div className="modal-prompt-content">
                      <div className="modal-prompt-text">{p.text}</div>
                      <div className="modal-prompt-meta">
                        <span className="modal-prompt-status">
                          <span className={`dot ${statusColor}`}></span>
                          {statusText}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <span className="modal-selected-count">
                {selectedPromptIds.length} selected
              </span>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => clearPromptSelection()}
                >
                  Clear
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowPromptModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};
